/**
 * The one demand no capability override may ever confer, whatever its source (FIQ-1926).
 *
 * This is a narrower rule than the SKU allowlist in grant-allowlist.mjs, and it exists for a
 * different reason. The allowlist answers "is this a sellable capability" and applies only to
 * hand-picked demands. This answers "would conferring it hand over the whole system", and applies
 * to EVERY source an override can draw on, auth bundles included.
 *
 * `admin` is special-cased rather than covered by the allowlist because of what it does:
 * `checkIndividualPermission` in library/authorization/index.mjs returns true for any demand that
 * is not `$`-prefixed the moment `admin` is present. It is not one capability among many — it is
 * every capability at once, including the `admin-advanced` gate on the Access tab itself. An
 * override that could confer it would let a holder of that gate mint themselves the gate's own
 * administrator, which no expiry date or audit record meaningfully constrains.
 *
 * Everything else an auth bundle contains still travels in full. A bundle is configured behind
 * the full-admin gate and selecting one deliberately delegates its authority; that delegation is
 * the point, and filtering it back down to SKU demands would make the same bundle mean different
 * things depending on where it was assigned. This carves out the single demand where delegation
 * is indistinguishable from escalation, and nothing else.
 *
 * Note it is the EXACT string. The `admin-*` family (`admin-users`, `admin-companies`, ...) are
 * ordinary scoped capabilities that grant only what they name, and are deliberately still
 * conferrable — blocking them would break bundles that legitimately delegate staff duties.
 */

export const NEVER_GRANTABLE_DEMANDS = Object.freeze(["admin"])

/**
 * Why it was refused, in the words the person choosing a bundle needs — they are not trying to
 * escalate anything, they are trying to sell a customer a capability, and "not allowed" without
 * a reason reads as a bug in the tool rather than a deliberate boundary.
 */
export const NEVER_GRANTABLE_REASON =
    "Holding admin satisfies every other permission in the system, so an override cannot confer it — " +
    "including the admin-advanced permission that grants access to this screen. " +
    "Everything else in the bundle is conferred as configured. " +
    "If this client genuinely needs a full administrator, assign the bundle to them directly, " +
    "which requires full admin and is a deliberate, separate act."

const never = new Set(NEVER_GRANTABLE_DEMANDS)

export function isNeverGrantable(demand) {
    return never.has(demand)
}

/**
 * Drops what may never be conferred. The READ path, where silently ignoring is correct — the row
 * may name a bundle that only later had `admin` added to it.
 */
export function filterNeverGrantable(demands) {
    return (demands ?? []).filter((demand) => !never.has(demand))
}

/**
 * Reports what would be refused, so the editor can say so BEFORE the override is saved rather
 * than leaving the admin to find out when the customer complains.
 */
export function findNeverGrantable(demands) {
    return [...new Set((demands ?? []).filter((demand) => never.has(demand)))]
}
